import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  RequirementActionResponse, RequirementApiResponse, RequirementListRequest,
  RequirementPageResponse, RequirementRecordResponse, RequirementRequest,
  RequirementStatusesResponse, RequirementSummaryRequest, RequirementSummaryResponse,
  RequirementViewResponse,
} from "../model/requirement.model";
import { BASE_PATH } from "../variables";

@Injectable({ providedIn: "root" })
export class StaffRequirementsService {
  private readonly requirementsUrl: string;

  constructor(private readonly http: HttpClient, @Inject(BASE_PATH) basePath: string) {
    this.requirementsUrl = `${basePath.replace(/\/$/, "")}/api/v1/requirements`;
  }

  list(request: RequirementListRequest): Observable<RequirementApiResponse<RequirementPageResponse>> {
    return this.http.get<RequirementApiResponse<RequirementPageResponse>>(this.requirementsUrl, {
      params: this.listParams(request),
    });
  }

  listMine(request: Omit<RequirementListRequest, "requesterUserId"> = {}): Observable<RequirementApiResponse<RequirementPageResponse>> {
    return this.http.get<RequirementApiResponse<RequirementPageResponse>>(`${this.requirementsUrl}/mine`, {
      params: this.listParams({ ...request, requesterUserId: undefined }),
    });
  }

  get(id: number): Observable<RequirementApiResponse<RequirementViewResponse>> {
    return this.http.get<RequirementApiResponse<RequirementViewResponse>>(`${this.requirementsUrl}/${id}`);
  }

  changeDocumentStatus(id: number, status: string, comment: string): Observable<RequirementApiResponse<RequirementRecordResponse>> {
    return this.http.patch<RequirementApiResponse<RequirementRecordResponse>>(
      `${this.requirementsUrl.replace('/api/v1/requirements', '/api/v1/internal/requirements')}/${id}/document-status`,
      { status, comment },
    );
  }

  create(request: RequirementRequest): Observable<RequirementApiResponse<RequirementRecordResponse>> {
    return this.http.post<RequirementApiResponse<RequirementRecordResponse>>(this.requirementsUrl, request);
  }

  update(id: number, request: RequirementRequest): Observable<RequirementApiResponse<RequirementRecordResponse>> {
    return this.http.put<RequirementApiResponse<RequirementRecordResponse>>(`${this.requirementsUrl}/${id}`, request);
  }

  delete(id: number): Observable<RequirementActionResponse> {
    return this.http.delete<RequirementActionResponse>(`${this.requirementsUrl}/${id}`);
  }

  listStatuses(): Observable<RequirementApiResponse<RequirementStatusesResponse>> {
    return this.http.get<RequirementApiResponse<RequirementStatusesResponse>>(`${this.requirementsUrl}/statuses`);
  }

  summary(request: RequirementSummaryRequest = {}): Observable<RequirementApiResponse<RequirementSummaryResponse>> {
    const params = this.optionalParams({
      companyId: request.companyId,
      requesterUserId: request.requesterUserId,
      month: request.month,
    });
    return this.http.get<RequirementApiResponse<RequirementSummaryResponse>>(`${this.requirementsUrl}/summary`, { params });
  }

  mineSummary(month?: string): Observable<RequirementApiResponse<RequirementSummaryResponse>> {
    const params = this.optionalParams({ month });
    return this.http.get<RequirementApiResponse<RequirementSummaryResponse>>(`${this.requirementsUrl}/mine/summary`, { params });
  }

  private listParams(request: RequirementListRequest): HttpParams {
    return this.optionalParams({
      companyId: request.companyId,
      departmentId: request.departmentId,
      requesterDepartmentId: request.requesterDepartmentId,
      requesterUserId: request.requesterUserId,
      requirementStatusId: request.requirementStatusId,
      requirementStatus: request.requirementStatus,
      paymentStatus: request.paymentStatus,
      documentStatus: request.documentStatus,
      documentStatuses: request.documentStatuses?.join(","),
      paymentDestination: request.paymentDestination,
      priorityLevelId: request.priorityLevelId,
      createdFrom: request.createdFrom,
      createdTo: request.createdTo,
      dueFrom: request.dueFrom,
      dueTo: request.dueTo,
      search: request.search,
      requesterName: request.requesterName,
      limit: request.limit,
      offset: request.offset,
    });
  }

  private optionalParams(values: Record<string, string | number | boolean | undefined>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== null && value !== "") params = params.set(key, String(value));
    }
    return params;
  }
}
